import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiCalendarLine,
  RiPrinterLine,
  RiDownloadLine,
  RiTimeLine,
  RiCloseCircleLine,
  RiMoneyDollarCircleLine,
  RiFileList3Line,
  RiArrowLeftRightLine,
  RiMailLine,
  RiWhatsappLine,
  RiInformationLine,
  RiAlertLine,
  RiRefundLine,
  RiShieldCheckLine,
} from "react-icons/ri";

const BusBookingConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    bus,
    searchQuery,
    selectedSeats,
    passengerCount,
    passengerDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    totalAmount,
    baseFare,
    convenienceFee,
    bookingStatus = "PAYMENT_SUCCESS", // Default to payment success
    bookingId,
    paymentId,
    ticketKey,
    orderId,
    message,
    amount,
    showSuccessIcon = true,
    pnr, 
    supplierBookingId,
    bookingNo 
  } = location.state || {};

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const options = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      };
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-IN", options);
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      PAYMENT_SUCCESS: {
        icon: RiCheckboxCircleLine,
        title: "Payment Successful!",
        subtitle: "Your payment was successful. Booking is being processed.",
        theme: "success",
        textColor: "text-success",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
      },
      CONFIRMED: {
        icon: RiCheckboxCircleLine,
        title: "Booking Confirmed!",
        subtitle: "Your bus tickets have been successfully booked",
        theme: "success",
        textColor: "text-success",
        showActions: true,
        showTracking: true,
        showContactSupport: false,
      },
      PAYMENT_SUCCESS_SEAT_FAILED: {
        icon: RiAlertLine,
        title: "Payment Successful - Seat Issue",
        subtitle:
          "Payment successful but seat booking failed. Our team will contact you.",
        theme: "warning",
        textColor: "text-warning",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
      },
      PENDING: {
        icon: RiTimeLine,
        title: "Booking Processing",
        subtitle: "Your booking is being processed. We'll update you shortly.",
        theme: "info",
        textColor: "text-info",
        showActions: true,
        showTracking: false,
        showContactSupport: true,
      },
      PAYMENT_FAILED: {
        icon: RiCloseCircleLine,
        title: "Payment Failed",
        subtitle: "Payment was not completed. Please try again.",
        theme: "danger",
        textColor: "text-danger",
        showActions: true,
        showTracking: false,
        showContactSupport: false,
      },
      PAYMENT_CANCELLED: {
        icon: RiCloseCircleLine,
        title: "Payment Cancelled",
        subtitle:
          "Payment was cancelled. Please try again if you wish to proceed.",
        theme: "secondary",
        textColor: "text-secondary",
        showActions: true,
        showTracking: false,
        showContactSupport: false,
      },
      PAYMENT_SUCCESS_PROCESSING_ERROR: {
        icon: RiErrorWarningLine,
        title: "Payment Successful - Processing Issue",
        subtitle:
          "Payment successful but we encountered an issue. Please contact support.",
        theme: "warning",
        textColor: "text-warning",
        showActions: true,
        showTracking: true,
        showContactSupport: true,
      },
    };

    return configs[status] || configs.PAYMENT_SUCCESS;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const printContent = document.getElementById("booking-details").innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  const handleNewBooking = () => {
    navigate("/bus/search");
  };

  const handleViewBookings = () => {
    navigate("/bus/bookinglist");
  };

  const handleContactSupport = () => {
    // You can implement contact support logic here
    const supportPhone = "+91-XXXXXXXXXX";
    const supportEmail = "support@example.com";

    const message = `Booking Reference: ${bookingId || "N/A"}\nPayment ID: ${
      paymentId || "N/A"
    }\nStatus: ${bookingStatus}`;

    const whatsappUrl = `https://wa.me/${supportPhone.replace(
      /\D/g,
      ""
    )}?text=${encodeURIComponent(message)}`;
    const mailtoUrl = `mailto:${supportEmail}?subject=Booking Support - ${bookingId}&body=${encodeURIComponent(
      message
    )}`;

    // Open support options
    const shouldCall = window.confirm(
      `Contact Support:\n\nPhone: ${supportPhone}\nEmail: ${supportEmail}\n\nClick OK to call, Cancel to email.`
    );

    if (shouldCall) {
      window.location.href = `tel:${supportPhone}`;
    } else {
      window.location.href = mailtoUrl;
    }
  };

  const statusConfig = getStatusConfig(bookingStatus);
  const StatusIcon = statusConfig.icon;

  // Generate booking reference if not provided
  const bookingReference = bookingId || `BUS${Date.now()}`;
  const displayMessage = message || statusConfig.subtitle;

  return (
    <div id="layout-wrapper">

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Action Buttons - Top Right */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <h3 className="mb-0">Booking Confirmation </h3>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    {statusConfig.showActions && (
                      <>
                        <button
                          className="btn btn-secondary d-flex align-items-center gap-2"
                          onClick={handlePrint}
                        >
                          <RiPrinterLine /> Print
                        </button>
                        <button
                          className="btn btn-success d-flex align-items-center gap-2"
                          onClick={handleDownload}
                        >
                          <RiDownloadLine /> Download
                        </button>

                        <button
                          className="btn btn-info"
                          onClick={handleViewBookings}
                        >
                          View All Bookings
                        </button>
                        <button
                          className="btn btn-primary d-flex align-items-center gap-2"
                          onClick={handleNewBooking}
                        >
                          <RiBusLine /> Book New Journey
                        </button>
                        {statusConfig.showContactSupport && (
                          <button
                            className="btn btn-warning d-flex align-items-center gap-2"
                            onClick={handleContactSupport}
                          >
                            <RiInformationLine /> Contact Support
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div id="booking-details">
              <div className="row justify-content-center">
                <div className="col-lg-12">
                  <div className="row">
                    {/* Status Banner */}
                    <div className="col-12">
                      <div
                        className={`alert alert-${statusConfig.theme} border-0 shadow-sm mb-4`}
                      >
                        <div className="row align-items-center">
                          <div className="col-md-8">
                            <div className="d-flex align-items-center gap-3">
                              <div className="bg-white rounded-circle p-3 shadow-sm">
                                <StatusIcon
                                  className={`fs-2 ${statusConfig.textColor}`}
                                />
                              </div>
                              <div>
                                <h4
                                  className={`mb-1 ${statusConfig.textColor}`}
                                >
                                  {statusConfig.title}
                                </h4>
                                <p className="mb-0">{displayMessage}</p>
                              </div>
                            </div>
                          </div>

                          {statusConfig.showTracking && (
                            <div className="col-md-4 text-md-end mt-3 mt-md-0">
                              <div className="d-flex flex-column gap-2">
                                <div className="alert alert-light mb-0">
                                  <small className="text-muted">
                                    <RiShieldCheckLine className="me-1" />
                                    Your booking is being tracked
                                  </small>
                                </div>
                                {bookingStatus === "PAYMENT_SUCCESS" && (
                                  <div className="alert alert-info mb-0">
                                    <small>
                                      <RiMailLine className="me-1" />
                                      Confirmation email will be sent shortly
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Left Column - Journey & Passenger Details */}
                    <div className="col-lg-8">
                      {/* Journey Overview Card */}
                      {bus && searchQuery && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header bg-white border-bottom-0 pb-0">
                            <h4 className="mb-3 d-flex align-items-center gap-2">
                              <RiBusLine className="text-primary" />
                              Journey Overview
                            </h4>
                          </div>
                          <div className="card-body">
                            <div className="row align-items-center mb-4">
                              <div className="col-md-5 text-center">
                                <div className="bg-light rounded p-3">
                                  <h5 className="text-primary mb-1">
                                    {searchQuery.fromStation}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    {bus.departureTime}
                                  </p>
                                </div>
                              </div>
                              <div className="col-md-2 text-center">
                                <RiArrowLeftRightLine className="text-muted fs-2" />
                                <div className="bg-primary text-white rounded-pill px-3 py-1 small">
                                  {bus.duration || "N/A"}
                                </div>
                              </div>
                              <div className="col-md-5 text-center">
                                <div className="bg-light rounded p-3">
                                  <h5 className="text-success mb-1">
                                    {searchQuery.toStation}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    {bus.arrivalTime}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <RiCalendarLine className="text-muted" />
                                  <div>
                                    <small className="text-muted">
                                      Travel Date
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {formatDate(searchQuery.travelDate)}
                                    </p>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <RiUserLine className="text-muted" />
                                  <div>
                                    <small className="text-muted">
                                      Passengers
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {passengerCount || 0} Passenger(s)
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <RiFileList3Line className="text-muted" />
                                  <div>
                                    <small className="text-muted">
                                      Bus Operator
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {bus.operatorName}
                                    </p>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <RiBusLine className="text-muted" />
                                  <div>
                                    <small className="text-muted">
                                      Bus Type
                                    </small>
                                    <p className="mb-0 fw-semibold">
                                      {bus.busType || "Standard"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Boarding & Dropping Points */}
                      {(selectedBoardingPoint || selectedDroppingPoint) && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header bg-white border-bottom-0">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiMapPinLine className="text-primary" />
                              Pickup & Drop Points
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              {selectedBoardingPoint && (
                                <div className="col-md-6">
                                  <div className="border-start border-3 border-primary ps-3">
                                    <h6 className="text-primary mb-2">
                                      Boarding Point
                                    </h6>
                                    <p className="fw-semibold mb-1">
                                      {selectedBoardingPoint.location}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <RiTimeLine className="me-1" />
                                      {selectedBoardingPoint.time}
                                    </p>
                                    <p className="text-muted small mb-0">
                                      {selectedBoardingPoint.address ||
                                        "Address not provided"}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {selectedDroppingPoint && (
                                <div className="col-md-6">
                                  <div className="border-start border-3 border-success ps-3">
                                    <h6 className="text-success mb-2">
                                      Dropping Point
                                    </h6>
                                    <p className="fw-semibold mb-1">
                                      {selectedDroppingPoint.location}
                                    </p>
                                    <p className="text-muted small mb-1">
                                      <RiTimeLine className="me-1" />
                                      {selectedDroppingPoint.time}
                                    </p>
                                    <p className="text-muted small mb-0">
                                      {selectedDroppingPoint.address ||
                                        "Address not provided"}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Passenger Details */}
                      {selectedSeats &&
                        passengerDetails &&
                        passengerDetails.length > 0 && (
                          <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white border-bottom-0">
                              <h5 className="mb-0 d-flex align-items-center gap-2">
                                <RiUserLine className="text-success" />
                                Passenger Details
                              </h5>
                            </div>
                            <div className="card-body">
                              <div className="table-responsive">
                                <table className="table table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Seat No</th>
                                      <th>Passenger Name</th>
                                      <th>Age</th>
                                      <th>ID Proof</th>
                                      <th>Bus Fare</th>
                                      <th>Conv. Fee</th>
                                      <th>Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedSeats.map((seat, index) => {
                                      const seatBaseFare =
                                        seat.totalPriceWithTaxes || 0;
                                      const seatConvFee =
                                        seat.convenienceFee || 100;
                                      const seatTotal =
                                        seatBaseFare + seatConvFee;
                                      const passenger =
                                        passengerDetails[index] ||
                                        passengerDetails[0];

                                      return (
                                        <tr key={seat.id}>
                                          <td>
                                            <span className="badge bg-primary fs-6">
                                              {seat.id}
                                            </span>
                                          </td>
                                          <td>
                                            <strong>
                                              {passenger?.title || "Mr/Ms"}{" "}
                                              {passenger?.firstName}{" "}
                                              {passenger?.lastName}
                                            </strong>
                                            <br />
                                            <small className="text-muted">
                                              {passenger?.email || "N/A"}
                                            </small>
                                          </td>
                                          <td>
                                            {passenger?.age || "N/A"} years
                                          </td>
                                          <td>
                                            <div>
                                              <small className="text-muted d-block">
                                                {passenger?.idProof || "N/A"}
                                              </small>
                                              {passenger?.idProofNumber && (
                                                <small className="text-muted d-block">
                                                  {passenger.idProofNumber.slice(
                                                    0,
                                                    8
                                                  )}
                                                  ...
                                                </small>
                                              )}
                                            </div>
                                          </td>
                                          <td>
                                            {formatCurrency(seatBaseFare)}
                                          </td>
                                          <td>{formatCurrency(seatConvFee)}</td>
                                          <td>
                                            <strong>
                                              {formatCurrency(seatTotal)}
                                            </strong>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>

                    {/* Right Column - Fare Summary & Important Info */}
                    <div className="col-lg-4">
                      {/* Fare Summary */}
                      {totalAmount && (
                        <div className="card border-0 shadow-sm mb-4">
                          <div className="card-header bg-white border-bottom-0">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                              <RiMoneyDollarCircleLine className="text-info" />
                              Fare Summary
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div>
                                <span className="text-muted">Bus Fare</span>
                                <div className="small text-muted">
                                  (Includes all taxes)
                                </div>
                              </div>
                              <span>{formatCurrency(baseFare)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div>
                                <span className="text-muted">
                                  Convenience Fee
                                </span>
                                <div className="small text-muted">
                                  (INR100 × {passengerCount || 1} seats)
                                </div>
                              </div>
                              <span>{formatCurrency(convenienceFee)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <strong className="fs-5">Total Amount</strong>
                              <strong className="fs-5 text-primary">
                                {formatCurrency(totalAmount)}
                              </strong>
                            </div>
                             
                                    <small className="d-block">
                                      <strong>Booking No:</strong> {bookingNo}
                                    </small>
                                 

                                  {/* Display PNR if available */}
                                  
                                    <small className="d-block">
                                      <strong>PNR:</strong> {pnr}
                                    </small>
                                  
                                  
                                    <small className="d-block">
                                      <strong>supplierBookingId:</strong> {supplierBookingId}
                                    </small>
                                 

                            {/* Payment Status */}
                            <div
                              className={`alert alert-${statusConfig.theme} mb-0`}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <StatusIcon />
                                <div>
                                  <strong className="d-block">
                                    Status: {bookingStatus.replace(/_/g, " ")}
                                  </strong>
                                  {paymentId && (
                                    <small className="d-block">
                                      Payment ID: {paymentId}
                                    </small>
                                  )}
                                  {amount && (
                                    <small className="d-block">
                                      Amount Paid: {formatCurrency(amount)}
                                    </small>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Status-specific Information */}
                      <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom-0">
                          <h6 className="mb-0 text-info">
                            <RiInformationLine className="me-2" />
                            Next Steps
                          </h6>
                        </div>
                        <div className="card-body">
                          {bookingStatus === "PAYMENT_SUCCESS" && (
                            <ul className="list-unstyled small mb-0">
                              <li className="mb-2 text-success">
                                <RiCheckboxCircleLine className="me-2" />
                                <strong>Payment confirmed</strong>
                              </li>
                              <li className="mb-2">
                                <RiMailLine className="me-2" />
                                Confirmation email will arrive within 5 minutes
                              </li>
                              <li className="mb-2">
                                <RiTimeLine className="me-2" />
                                Seats are being reserved for you
                              </li>
                              <li className="mb-0">
                                <RiShieldCheckLine className="me-2" />
                                Your booking is 100% secure
                              </li>
                            </ul>
                          )}

                          {bookingStatus === "PAYMENT_SUCCESS_SEAT_FAILED" && (
                            <ul className="list-unstyled small mb-0">
                              <li className="mb-2 text-success">
                                <RiCheckboxCircleLine className="me-2" />
                                <strong>Payment successful</strong>
                              </li>
                              <li className="mb-2 text-warning">
                                <RiAlertLine className="me-2" />
                                <strong>Seat reservation pending</strong>
                              </li>
                              <li className="mb-2">
                                <RiMailLine className="me-2" />
                                Our team will contact you within 2 hours
                              </li>
                              <li className="mb-2">
                                <RiRefundLine className="me-2" />
                                Full refund if seats unavailable
                              </li>
                              <li className="mb-0">
                                <RiWhatsappLine className="me-2" />
                                Contact support for immediate assistance
                              </li>
                            </ul>
                          )}

                          {bookingStatus === "CONFIRMED" && (
                            <ul className="list-unstyled small mb-0">
                              <li className="mb-2 text-success">
                                <RiCheckboxCircleLine className="me-2" />
                                <strong>Booking confirmed</strong>
                              </li>
                              <li className="mb-2">
                                <RiMailLine className="me-2" />
                                E-ticket sent to your email
                              </li>
                              <li className="mb-2">
                                <RiUserLine className="me-2" />
                                Carry valid ID proof during travel
                              </li>
                              <li className="mb-0">
                                <RiTimeLine className="me-2" />
                                Arrive 30 minutes before departure
                              </li>
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* Important Information */}
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-bottom-0">
                          <h6 className="mb-0 text-warning">
                            <RiErrorWarningLine className="me-2" />
                            Important Information
                          </h6>
                        </div>
                        <div className="card-body">
                          <ul className="list-unstyled small mb-0">
                            <li className="mb-2">
                              • Please arrive at the boarding point 30 minutes
                              before departure
                            </li>
                            <li className="mb-2">
                              • Carry a valid government-issued ID proof
                            </li>
                            <li className="mb-2">
                              • Print or download your e-ticket for verification
                            </li>
                            <li className="mb-2">
                              • For assistance, contact: +91-XXXXXXXXXX
                            </li>
                            <li className="mb-0">
                              • For cancellations, refer to our cancellation
                              policy
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          .btn,
          .d-print-none {
            display: none !important;
          }
          .card {
            border: 1px solid #dee2e6 !important;
            page-break-inside: avoid;
          }
          .shadow-sm {
            box-shadow: none !important;
          }
          .alert {
            border: 1px solid #dee2e6 !important;
          }
        }

        @media (max-width: 768px) {
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 20px;
          }

          .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }

          .col-md-5,
          .col-md-2,
          .col-md-5 {
            margin-bottom: 15px;
          }
        }

        .badge {
          font-size: 0.75em;
        }
      `}</style>
    </div>
  );
};

export default BusBookingConfirmation;
